<template>
  <div>
    <!-- VALID ARTICLE -->
    <template v-if="article">
      <div class="mt-24 mb-16">
        <UiHeroTop :article="{ title: article.title, excerpthtml: '<h1>' + article.title + '</h1>', image: article.image}" />
      </div>

      <UiExtraNarrowContent>
        <div class="content-html" v-html="article.contenthtml"></div>

        <div class="grid grid-cols-1 sm:grid-cols-3 items-center">
          <div class="flex items-center">
            <p class="text-xs sm:mt-5">
              Published on {{ formatDate(article.publishedAt) }}
            </p>
          </div>

          <div class="flex items-center">
            <p class="text-xs sm:mt-5">
              Words by: {{ article.wordsBy }}
            </p>
          </div>

          <div class="flex justify-start sm:justify-end items-center">
            <UiShareButton :article="article">
              Share
              <IconShare class="inline-block h-5 w-5 ml-1" stroke="1.25" />
            </UiShareButton>
          </div>
        </div>
      </UiExtraNarrowContent>

      <div class="mt-32">
        <UiNarrowContent>
          <h4 class="large">Latest news</h4>
        </UiNarrowContent>

        <ContentNewsSection :articles="filteredArticles" :tag="null" :small="false" :openedHandle="null" />

        <UiNarrowContent class="mt-12">
          <NuxtLink to="/news/">
            <UiButton class="btn lightbrown text-white px-8 py-4 rounded-md mt-12 mb-16">
              Back to all news posts
            </UiButton>
          </NuxtLink>
        </UiNarrowContent>
      </div>
    </template>

    <!-- 404 FALLBACK -->
    <template v-else>
      <UiContentwrapper class="mt-36">
        <UiFullcolumn>
          <h1>404</h1>
          <p>News article not found</p>
            <NuxtLink to="/news">
            <UiButton class="btn lightbrown text-white px-8 py-4 rounded-md mt-8 mb-8">
              Back to all news posts
            </UiButton>
            </NuxtLink>
        </UiFullcolumn>
      </UiContentwrapper>
    </template>
  </div>
</template>


<script setup>
import { useBlogStore } from '@/stores/blogStore';
import { ref, computed } from 'vue';
import { useRoute } from 'vue-router';
const { getSeoArticleMeta } = useImageUtils();
import { IconShare } from '@tabler/icons-vue';

const blogStore = useBlogStore();
const route = useRoute();
const slug = ref(route.params.slug);
const openedHandle = ref(route.query.article || null);

const { data: blog } = await useAsyncData('blog', async () => {  
  return await blogStore.fetchBlogByHandle('news');
});

const article = computed(() => {
  return blog.value?.articles?.find(article => article.handle === slug.value);
});

const filteredArticles = computed(() => {
  return blog.value?.articles
    ?.filter(a => !(a.tags?.includes('Excluded')) && a.handle !== slug.value)
    .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt))
    .slice(0, 3) || [];
});

// Format date helper
const formatDate = (date) =>
  new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });

// SEO (only apply if valid)
if (article.value) {
  useNewsArticleSeo(article.value);
}
</script>


<style scoped>

/* Videos inside article content should match text width */
::v-deep(.content-html p:has(iframe:only-child)),
::v-deep(.content-html div:has(iframe:only-child)) {
  max-width: 48rem;
  margin: 76px auto;
  text-align: center;
}

/* Style the iframe inside <p> or <div> */
::v-deep(.content-html p:has(iframe:only-child) iframe),
::v-deep(.content-html div:has(iframe:only-child) iframe) {
  width: 100%;
  max-width: 100%;
  aspect-ratio: 16 / 9;
  display: block;
  border: none;
  object-fit: cover;
}

@media (max-width: 640px) {
  ::v-deep(.content-html p:has(iframe:only-child)),
  ::v-deep(.content-html div:has(iframe:only-child)) {
    max-width: 100%;
    margin: 40px auto;
  }

  ::v-deep(.content-html p:has(iframe:only-child) iframe),
  ::v-deep(.content-html div:has(iframe:only-child) iframe) {
    width: 100%;
    max-width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
}

@media (min-width: 640px) and (max-width: 1024px) {
  ::v-deep(.content-html p:has(iframe:only-child)),
  ::v-deep(.content-html div:has(iframe:only-child)) {
    max-width: 100%; 
    margin: 60px auto;
  }

  ::v-deep(.content-html p:has(iframe:only-child) iframe),
  ::v-deep(.content-html div:has(iframe:only-child) iframe) {
    width: 100%;
    max-width: 100%;
    height: auto;
    aspect-ratio: 16 / 9;
    object-fit: cover;
  }
}

</style>